import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import '../assets/home.css';
import '../assets/home-rwd.css';
import '../assets/homev2.css';
import '../assets/homev3.css';

const HomeV3 = () => {
    const canvasRef = useRef(null);
    const sceneRef = useRef({
        scene: null,
        camera: null,
        renderer: null,
        controls: null,
        tree: null,
        treeParts: [],
        growth: {
            height: 2,
            width: 0.3,
            branches: 0,
            leaves: 0,
            flowers: 0,
            fruits: 0,
            maxHeight: 20,
            maxWidth: 2.0,
            maxBranches: 30,
            maxLeaves: 200,
            maxFlowers: 80,
            maxFruits: 30,
        }
    });
    
    const [waterLevel, setWaterLevel] = useState(50);
    const [sunLevel, setSunLevel] = useState(50);
    const [fertilizerLevel, setFertilizerLevel] = useState(50);
    const [treeStats, setTreeStats] = useState({
        height: 2,
        branches: 0,
        leaves: 0,
        flowers: 0,
        fruits: 0,
        health: 100
    });
    
    // Function to create the initial tree - moved inside useEffect
    const createTree = (scene) => {
        const { growth } = sceneRef.current;
        const treeParts = [];
        
        // Create the trunk with more detailed geometry
        const trunkGeometry = new THREE.CylinderGeometry(growth.width, growth.width * 1.2, growth.height, 12, 6);
        
        // More realistic bark material
        const trunkMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x8B4513,
            roughness: 0.9,
            metalness: 0.1,
            bumpScale: 0.05
        });
        
        const trunk = new THREE.Mesh(trunkGeometry, trunkMaterial);
        trunk.position.y = growth.height / 2 - 0.5;
        trunk.castShadow = true;
        trunk.receiveShadow = true;
        scene.add(trunk);
        treeParts.push({ type: 'trunk', mesh: trunk });
        
        // Create an empty object to hold all tree parts
        const treeObject = new THREE.Object3D();
        treeObject.add(trunk);
        scene.add(treeObject);
        
        // Update sceneRef with tree parts
        sceneRef.current = {
            ...sceneRef.current,
            scene,
            tree: treeObject,
            treeParts
        };
        
        // Update initial stats
        setTreeStats({
            height: growth.height,
            branches: 0,
            leaves: 0,
            flowers: 0,
            fruits: 0,
            health: 100
        });
    };
    
    // Initialize the 3D scene
    useEffect(() => {
        if (!canvasRef.current) return;
        
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0x87CEEB); // Sky blue background
        
        // Setup camera
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        camera.position.set(0, 5, 12);
        
        // Setup renderer
        const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, antialias: true });
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.shadowMap.enabled = true;
        
        // Setup orbit controls for camera movement
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.minDistance = 3;
        controls.maxDistance = 30;
        controls.maxPolarAngle = Math.PI / 2;
        
        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
        scene.add(ambientLight);
        
        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);
        directionalLight.position.set(5, 10, 5);
        directionalLight.castShadow = true;
        directionalLight.shadow.mapSize.width = 2048;
        directionalLight.shadow.mapSize.height = 2048;
        scene.add(directionalLight);
        
        // Create ground
        const groundGeometry = new THREE.PlaneGeometry(50, 50);
        const groundMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x558833,
            roughness: 0.8,
            metalness: 0.2
        });
        const ground = new THREE.Mesh(groundGeometry, groundMaterial);
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.5;
        ground.receiveShadow = true;
        scene.add(ground);
        
        // Store references first, before creating tree
        sceneRef.current = {
            ...sceneRef.current,
            scene,
            camera,
            renderer,
            controls
        };
        
        // Initialize tree after scene is set
        createTree(scene);
        
        // Handle window resize
        const handleResize = () => {
            const { camera, renderer } = sceneRef.current;
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        
        window.addEventListener('resize', handleResize);
        
        // Animation loop
        const animate = () => {
            requestAnimationFrame(animate);
            sceneRef.current.controls.update();
            sceneRef.current.renderer.render(sceneRef.current.scene, sceneRef.current.camera);
        };
        
        animate();
        
        return () => {
            window.removeEventListener('resize', handleResize);
            // Clean up resources
            renderer.dispose();
            
            // Remove all objects from the scene
            while(scene.children.length > 0) { 
                const object = scene.children[0];
                scene.remove(object);
            }
        };
    }, []);
    
    // Function to grow the tree
    const growTree = () => {
        const { growth, treeParts, scene } = sceneRef.current;
        
        if (!scene || !treeParts || treeParts.length === 0) return;
        
        const oldHeight = growth.height;
        
        // Calculate growth factors based on water, sun, and fertilizer
        const waterFactor = waterLevel / 100;
        const sunFactor = sunLevel / 100;
        const fertilizerFactor = fertilizerLevel / 100;
        
        // Calculate random growth amounts - significantly increased for faster growth
        const heightGrowth = Math.random() * 2.0 * waterFactor * sunFactor;
        const widthGrowth = Math.random() * 0.2 * fertilizerFactor;
        const branchGrowthChance = Math.random() * fertilizerFactor * sunFactor;
        const leafGrowthChance = Math.random() * sunFactor * waterFactor;
        const flowerGrowthChance = Math.random() * fertilizerFactor * sunFactor * waterFactor;
        const fruitGrowthChance = flowerGrowthChance * 0.8; // Fruits grow from flowers
        
        // Apply growth limits
        growth.height = Math.min(growth.height + heightGrowth, growth.maxHeight);
        growth.width = Math.min(growth.width + widthGrowth, growth.maxWidth);
        
        // Update trunk size
        const trunkPart = treeParts.find(part => part.type === 'trunk');
        if (trunkPart) {
            const trunk = trunkPart.mesh;
            scene.remove(trunk);
            
            const newTrunkGeometry = new THREE.CylinderGeometry(
                growth.width, 
                growth.width * 1.2, 
                growth.height, 
                12, 
                6
            );
            
            const newTrunk = new THREE.Mesh(newTrunkGeometry, trunk.material);
            newTrunk.position.y = growth.height / 2 - 0.5;
            newTrunk.castShadow = true;
            newTrunk.receiveShadow = true;
            scene.add(newTrunk);
            
            treeParts[0] = { type: 'trunk', mesh: newTrunk };
        }
        
        // Add multiple growth elements in a single cycle for faster visible growth
        
        // Add new branches
        if (branchGrowthChance > 0.4 && growth.branches < growth.maxBranches) {
            const branchCount = Math.floor(Math.random() * 3) + 2; // 2-4 branches at once
            for (let i = 0; i < branchCount; i++) {
                if (growth.branches < growth.maxBranches) {
                    addBranch(growth.height);
                    growth.branches++;
                }
            }
        }
        
        // Add new leaves
        if (leafGrowthChance > 0.3 && growth.leaves < growth.maxLeaves) {
            const leafCount = Math.floor(Math.random() * 8) + 5; // 5-12 leaves at once
            for (let i = 0; i < leafCount; i++) {
                if (growth.leaves < growth.maxLeaves) {
                    addLeaf();
                    growth.leaves++;
                }
            }
        }
        
        // Add new flowers
        if (flowerGrowthChance > 0.5 && growth.flowers < growth.maxFlowers) {
            const flowerCount = Math.floor(Math.random() * 5) + 2; // 2-6 flowers at once
            for (let i = 0; i < flowerCount; i++) {
                if (growth.flowers < growth.maxFlowers) {
                    addFlower();
                    growth.flowers++;
                }
            }
        }
        
        // Add fruits (if there are flowers)
        if (fruitGrowthChance > 0.7 && growth.flowers > 5 && growth.fruits < growth.maxFruits) {
            const fruitCount = Math.floor(Math.random() * 3) + 1;
            for (let i = 0; i < fruitCount; i++) {
                if (growth.fruits < growth.maxFruits) {
                    addFruit();
                    growth.fruits++;
                }
            }
        }
        
        // Update stats
        setTreeStats({
            height: parseFloat(growth.height.toFixed(2)),
            branches: growth.branches,
            leaves: growth.leaves,
            flowers: growth.flowers,
            fruits: growth.fruits,
            health: 100 // Can be modified based on other factors
        });
    };
    
    // Function to add a branch with more complexity
    const addBranch = (trunkHeight) => {
        const { scene, treeParts, growth } = sceneRef.current;
        
        if (!scene) return;
        
        // Random position on the trunk
        const heightPosition = Math.random() * (trunkHeight - 1) + 0.5;
        const angle = Math.random() * Math.PI * 2;
        const length = Math.random() * 3 + 1.5; // Longer branches
        const thickness = growth.width * 0.6;
        
        // Create branch geometry
        const branchGeometry = new THREE.CylinderGeometry(
            thickness * 0.5, 
            thickness, 
            length, 
            8
        );
        
        const branchMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x8B4513,
            roughness: 0.9,
            metalness: 0.1
        });
        
        const branch = new THREE.Mesh(branchGeometry, branchMaterial);
        
        // Position branch
        branch.position.set(0, heightPosition, 0);
        
        // Rotate branch outward from trunk
        branch.rotation.z = Math.PI / 2;
        branch.rotation.y = angle;
        
        // Adjust position after rotation
        branch.translateY(length / 2);
        
        branch.castShadow = true;
        branch.receiveShadow = true;
        scene.add(branch);
        
        treeParts.push({ type: 'branch', mesh: branch });
        
        // Add some leaves to the branch
        const leafCount = Math.floor(Math.random() * 8) + 5;
        for (let i = 0; i < leafCount; i++) {
            if (growth.leaves < growth.maxLeaves) {
                addLeafToBranch(branch, length);
                growth.leaves++;
            }
        }
        
        // Add some flowers to the branch
        if (Math.random() > 0.5) {
            const flowerCount = Math.floor(Math.random() * 4) + 1;
            for (let i = 0; i < flowerCount; i++) {
                if (growth.flowers < growth.maxFlowers) {
                    addFlowerToBranch(branch, length);
                    growth.flowers++;
                }
            }
        }
        
        // Possibly add a sub-branch for more complexity
        if (Math.random() > 0.7 && length > 2 && growth.branches < growth.maxBranches) {
            addSubBranch(branch, length);
            growth.branches++;
        }
    };
    
    // Function to add sub-branches for more realism
    const addSubBranch = (parentBranch, parentLength) => {
        const { scene, treeParts, growth } = sceneRef.current;
        
        if (!scene) return;
        
        // Position along the parent branch (toward the end)
        const length = parentLength * 0.6;
        const thickness = growth.width * 0.3;
        
        // Create geometry
        const branchGeometry = new THREE.CylinderGeometry(
            thickness * 0.3, 
            thickness, 
            length, 
            6
        );
        
        const branchMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x8B4513,
            roughness: 0.9,
            metalness: 0.1
        });
        
        const branch = new THREE.Mesh(branchGeometry, branchMaterial);
        
        // Position the sub-branch relative to parent
        branch.position.copy(parentBranch.position);
        branch.position.y += parentLength * 0.7; // Position toward end of parent
        
        // Rotate the sub-branch
        branch.rotation.copy(parentBranch.rotation);
        branch.rotateZ(Math.random() * Math.PI/2 - Math.PI/4); // -45 to 45 degrees
        
        branch.castShadow = true;
        branch.receiveShadow = true;
        scene.add(branch);
        
        treeParts.push({ type: 'branch', mesh: branch });
        
        // Add some leaves to the sub-branch
        const leafCount = Math.floor(Math.random() * 6) + 3;
        for (let i = 0; i < leafCount; i++) {
            if (growth.leaves < growth.maxLeaves) {
                addLeafToBranch(branch, length);
                growth.leaves++;
            }
        }
    };
    
    // Enhanced leaf to branch function
    const addLeafToBranch = (branch, branchLength) => {
        const { scene, treeParts } = sceneRef.current;
        
        if (!scene || !branch) return;
        
        // Create more realistic leaf shape instead of sphere
        const leafShape = new THREE.Shape();
        
        // Leaf outline points (more detailed than a simple sphere)
        leafShape.moveTo(0, 0);
        leafShape.bezierCurveTo(0.1, 0.2, 0.3, 0.3, 0.5, 0.3);
        leafShape.bezierCurveTo(0.7, 0.3, 0.9, 0.2, 1, 0);
        leafShape.bezierCurveTo(0.9, -0.2, 0.7, -0.3, 0.5, -0.3);
        leafShape.bezierCurveTo(0.3, -0.3, 0.1, -0.2, 0, 0);
        
        const leafGeometry = new THREE.ShapeGeometry(leafShape);
        
        // Randomize leaf colors (various greens)
        const green = Math.random() * 0.15 + 0.25; // 0.25-0.4
        const leafMaterial = new THREE.MeshStandardMaterial({ 
            color: new THREE.Color(0.1, green, 0.1),
            roughness: 0.7,
            metalness: 0.1,
            side: THREE.DoubleSide // render both sides
        });
        
        const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
        
        // Position leaf somewhere along the branch
        const positionOnBranch = Math.random() * branchLength * 0.8 + branchLength * 0.2;
        
        leaf.position.copy(branch.position);
        
        // Apply branch rotation to leaf position
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(branch.rotation);
        const posVector = new THREE.Vector3(0, positionOnBranch, 0);
        posVector.applyMatrix4(rotationMatrix);
        
        leaf.position.add(posVector);
        
        // Scale to appropriate size
        const leafSize = 0.3 + Math.random() * 0.2;
        leaf.scale.set(leafSize, leafSize, leafSize);
        
        // Random leaf rotation for natural look
        leaf.rotation.x = Math.random() * Math.PI;
        leaf.rotation.y = Math.random() * Math.PI;
        leaf.rotation.z = Math.random() * Math.PI;
        
        leaf.castShadow = true;
        scene.add(leaf);
        
        treeParts.push({ type: 'leaf', mesh: leaf });
    };
    
    // Enhanced leaf function for trunk leaves
    const addLeaf = () => {
        const { scene, treeParts, growth } = sceneRef.current;
        
        if (!scene) return;
        
        // Create cluster of leaves for more realism
        const leafGroup = new THREE.Group();
        
        // Generate 2-4 leaves in a cluster
        const leafCount = Math.floor(Math.random() * 3) + 2;
        for (let i = 0; i < leafCount; i++) {
            // Create more realistic leaf shape
            const leafShape = new THREE.Shape();
            
            // Leaf outline
            leafShape.moveTo(0, 0);
            leafShape.bezierCurveTo(0.1, 0.2, 0.3, 0.3, 0.5, 0.3);
            leafShape.bezierCurveTo(0.7, 0.3, 0.9, 0.2, 1, 0);
            leafShape.bezierCurveTo(0.9, -0.2, 0.7, -0.3, 0.5, -0.3);
            leafShape.bezierCurveTo(0.3, -0.3, 0.1, -0.2, 0, 0);
            
            const leafGeometry = new THREE.ShapeGeometry(leafShape);
            
            // Randomize leaf colors (various greens)
            const green = Math.random() * 0.15 + 0.25; // 0.25-0.4
            const leafMaterial = new THREE.MeshStandardMaterial({ 
                color: new THREE.Color(0.1, green, 0.1),
                roughness: 0.7,
                metalness: 0.1,
                side: THREE.DoubleSide
            });
            
            const leaf = new THREE.Mesh(leafGeometry, leafMaterial);
            
            // Position around center point of the cluster
            leaf.position.set(
                (Math.random() - 0.5) * 0.2,
                (Math.random() - 0.5) * 0.2,
                (Math.random() - 0.5) * 0.2
            );
            
            // Random rotation for each leaf
            leaf.rotation.x = Math.random() * Math.PI;
            leaf.rotation.y = Math.random() * Math.PI;
            leaf.rotation.z = Math.random() * Math.PI;
            
            // Scale leaf
            const leafSize = 0.3 + Math.random() * 0.2;
            leaf.scale.set(leafSize, leafSize, leafSize);
            
            leaf.castShadow = true;
            leafGroup.add(leaf);
        }
        
        // Position leaf cluster on the trunk
        const heightPosition = Math.random() * (growth.height - 1) + 0.5;
        const angle = Math.random() * Math.PI * 2;
        const distance = growth.width + 0.2;
        
        leafGroup.position.set(
            Math.cos(angle) * distance,
            heightPosition,
            Math.sin(angle) * distance
        );
        
        // Rotate the whole cluster for variety
        leafGroup.rotation.x = Math.random() * Math.PI;
        leafGroup.rotation.y = Math.random() * Math.PI;
        leafGroup.rotation.z = Math.random() * Math.PI;
        
        scene.add(leafGroup);
        treeParts.push({ type: 'leaf-cluster', mesh: leafGroup });
    };
    
    // Function to add a fruit to the tree
    const addFruit = () => {
        const { scene, treeParts, growth } = sceneRef.current;
        
        if (!scene) return;
        
        // Create fruit
        const fruitColors = [
            0xFF6347, // Tomato red
            0xFF4500, // Orange red
            0xFFA500, // Orange
            0xFFD700, // Gold
            0xFF0000, // Red
        ];
        
        const randomColor = fruitColors[Math.floor(Math.random() * fruitColors.length)];
        
        const fruitGeometry = new THREE.SphereGeometry(0.2, 12, 12);
        const fruitMaterial = new THREE.MeshStandardMaterial({ 
            color: randomColor,
            roughness: 0.5,
            metalness: 0.1
        });
        
        const fruit = new THREE.Mesh(fruitGeometry, fruitMaterial);
        
        // Add stem to the fruit
        const stemGeometry = new THREE.CylinderGeometry(0.03, 0.03, 0.1, 8);
        const stemMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x556B2F, // Dark olive green
            roughness: 0.9,
            metalness: 0.1
        });
        
        const stem = new THREE.Mesh(stemGeometry, stemMaterial);
        stem.position.y = 0.15;
        
        const fruitGroup = new THREE.Group();
        fruitGroup.add(fruit);
        fruitGroup.add(stem);
        
        // Position fruit somewhere on the tree - prefer branches with flowers
        if (Math.random() > 0.3 && treeParts.filter(part => part.type === 'branch').length > 0) {
            // Put on branch
            const branches = treeParts.filter(part => part.type === 'branch');
            const randomBranch = branches[Math.floor(Math.random() * branches.length)];
            const branch = randomBranch.mesh;
            
            const branchLength = 2; // Approximate branch length
            const positionOnBranch = Math.random() * branchLength * 0.7 + branchLength * 0.3;
            
            fruitGroup.position.copy(branch.position);
            
            // Apply branch rotation to fruit position
            const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(branch.rotation);
            const posVector = new THREE.Vector3(0, positionOnBranch, 0);
            posVector.applyMatrix4(rotationMatrix);
            
            // Add slight offset to hang below branch
            posVector.y -= 0.2;
            
            fruitGroup.position.add(posVector);
        } else {
            // Put directly on trunk
            const heightPosition = Math.random() * (growth.height - 1) + 0.5;
            const angle = Math.random() * Math.PI * 2;
            const distance = growth.width + 0.4;
            
            fruitGroup.position.set(
                Math.cos(angle) * distance,
                heightPosition,
                Math.sin(angle) * distance
            );
        }
        
        // Scale variation
        const scale = 0.7 + Math.random() * 0.6;
        fruitGroup.scale.set(scale, scale, scale);
        
        fruitGroup.castShadow = true;
        scene.add(fruitGroup);
        
        treeParts.push({ type: 'fruit', mesh: fruitGroup });
    };
    
    // Function to add a flower to a branch
    const addFlowerToBranch = (branch, branchLength) => {
        const { scene, treeParts } = sceneRef.current;
        
        if (!scene || !branch) return;
        
        // Create flower center
        const flowerCenterGeometry = new THREE.SphereGeometry(0.15, 8, 8);
        const flowerCenterMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xFFD700, // Gold
            roughness: 0.5,
            metalness: 0.2
        });
        
        const flowerCenter = new THREE.Mesh(flowerCenterGeometry, flowerCenterMaterial);
        
        // Create flower petals
        const petalGroup = new THREE.Group();
        const petalColors = [0xFF69B4, 0xFF1493, 0xDDA0DD, 0xEE82EE, 0xDA70D6, 0xBA55D3];
        const randomColor = petalColors[Math.floor(Math.random() * petalColors.length)];
        
        const petalCount = 5 + Math.floor(Math.random() * 4);  // 5-8 petals
        
        for (let i = 0; i < petalCount; i++) {
            const petalGeometry = new THREE.SphereGeometry(0.2, 4, 4);
            const petalMaterial = new THREE.MeshStandardMaterial({ 
                color: randomColor,
                roughness: 0.6,
                metalness: 0.1
            });
            
            const petal = new THREE.Mesh(petalGeometry, petalMaterial);
            const angle = (i / petalCount) * Math.PI * 2;
            const distance = 0.2;
            
            petal.position.set(
                Math.cos(angle) * distance,
                0,
                Math.sin(angle) * distance
            );
            
            petal.scale.set(1, 0.5, 1);
            petalGroup.add(petal);
        }
        
        // Create complete flower
        const flower = new THREE.Group();
        flower.add(flowerCenter);
        flower.add(petalGroup);
        
        // Position flower somewhere along the branch
        const positionOnBranch = Math.random() * branchLength * 0.8 + branchLength * 0.2;
        
        flower.position.copy(branch.position);
        
        // Apply branch rotation to flower position
        const rotationMatrix = new THREE.Matrix4().makeRotationFromEuler(branch.rotation);
        const posVector = new THREE.Vector3(0, positionOnBranch, 0);
        posVector.applyMatrix4(rotationMatrix);
        
        flower.position.add(posVector);
        
        // Random flower rotation
        flower.rotation.x = Math.random() * Math.PI * 2;
        flower.rotation.y = Math.random() * Math.PI * 2;
        flower.rotation.z = Math.random() * Math.PI * 2;
        
        flower.scale.set(0.6, 0.6, 0.6);
        
        flower.castShadow = true;
        scene.add(flower);
        
        treeParts.push({ type: 'flower', mesh: flower });
    };
    
    // Function to add a flower (directly on trunk)
    const addFlower = () => {
        const { scene, treeParts, growth } = sceneRef.current;
        
        if (!scene) return;
        
        // Create flower center
        const flowerCenterGeometry = new THREE.SphereGeometry(0.15, 8, 8);
        const flowerCenterMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xFFD700, // Gold
            roughness: 0.5,
            metalness: 0.2
        });
        
        const flowerCenter = new THREE.Mesh(flowerCenterGeometry, flowerCenterMaterial);
        
        // Create flower petals
        const petalGroup = new THREE.Group();
        const petalColors = [0xFF69B4, 0xFF1493, 0xDDA0DD, 0xEE82EE, 0xDA70D6, 0xBA55D3];
        const randomColor = petalColors[Math.floor(Math.random() * petalColors.length)];
        
        const petalCount = 5 + Math.floor(Math.random() * 4);  // 5-8 petals
        
        for (let i = 0; i < petalCount; i++) {
            const petalGeometry = new THREE.SphereGeometry(0.2, 4, 4);
            const petalMaterial = new THREE.MeshStandardMaterial({ 
                color: randomColor,
                roughness: 0.6,
                metalness: 0.1
            });
            
            const petal = new THREE.Mesh(petalGeometry, petalMaterial);
            const angle = (i / petalCount) * Math.PI * 2;
            const distance = 0.2;
            
            petal.position.set(
                Math.cos(angle) * distance,
                0,
                Math.sin(angle) * distance
            );
            
            petal.scale.set(1, 0.5, 1);
            petalGroup.add(petal);
        }
        
        // Create complete flower
        const flower = new THREE.Group();
        flower.add(flowerCenter);
        flower.add(petalGroup);
        
        // Position flower somewhere on the trunk
        const heightPosition = Math.random() * (growth.height - 1) + 0.5;
        const angle = Math.random() * Math.PI * 2;
        const distance = growth.width + 0.3;
        
        flower.position.set(
            Math.cos(angle) * distance,
            heightPosition,
            Math.sin(angle) * distance
        );
        
        // Random flower rotation
        flower.rotation.x = Math.random() * Math.PI * 2;
        flower.rotation.y = Math.random() * Math.PI * 2;
        flower.rotation.z = Math.random() * Math.PI * 2;
        
        flower.scale.set(0.5, 0.5, 0.5);
        
        flower.castShadow = true;
        scene.add(flower);
        
        treeParts.push({ type: 'flower', mesh: flower });
    };
    
    // Multiple growth calls for faster effect
    const handleWater = () => {
        setWaterLevel(100);
        growTree();
        growTree();
        growTree();
        
        // Gradually decrease water level
        setTimeout(() => setWaterLevel(prev => Math.max(prev - 15, 0)), 1000);
        setTimeout(() => setWaterLevel(prev => Math.max(prev - 15, 0)), 2000);
        setTimeout(() => setWaterLevel(prev => Math.max(prev - 10, 0)), 3000);
    };
    
    const handleSunshine = () => {
        setSunLevel(100);
        growTree();
        growTree();
        growTree();
        
        // Gradually decrease sun level
        setTimeout(() => setSunLevel(prev => Math.max(prev - 15, 0)), 1000);
        setTimeout(() => setSunLevel(prev => Math.max(prev - 15, 0)), 2000);
        setTimeout(() => setSunLevel(prev => Math.max(prev - 10, 0)), 3000);
    };
    
    const handleFertilize = () => {
        setFertilizerLevel(100);
        growTree();
        growTree();
        growTree();
        
        // Gradually decrease fertilizer level
        setTimeout(() => setFertilizerLevel(prev => Math.max(prev - 15, 0)), 1000);
        setTimeout(() => setFertilizerLevel(prev => Math.max(prev - 15, 0)), 2000);
        setTimeout(() => setFertilizerLevel(prev => Math.max(prev - 10, 0)), 3000);
    };
    
    return (
        <div className="homev3-container">
            <canvas ref={canvasRef} className="tree-canvas" />
            
            <div className="controls-container">
                <div className="tree-stats">
                    <h3>Tree Stats</h3>
                    <p>Height: {treeStats.height.toFixed(2)} m</p>
                    <p>Branches: {treeStats.branches}</p>
                    <p>Leaves: {treeStats.leaves}</p>
                    <p>Flowers: {treeStats.flowers}</p>
                    <p>Fruits: {treeStats.fruits}</p>
                    <p>Health: {treeStats.health}%</p>
                </div>
                
                <div className="resource-bars">
                    <div className="resource-bar">
                        <label>Water</label>
                        <div className="bar-container">
                            <div className="bar-fill water" style={{ width: `${waterLevel}%` }}></div>
                        </div>
                        <span>{waterLevel}%</span>
                    </div>
                    
                    <div className="resource-bar">
                        <label>Sunlight</label>
                        <div className="bar-container">
                            <div className="bar-fill sun" style={{ width: `${sunLevel}%` }}></div>
                        </div>
                        <span>{sunLevel}%</span>
                    </div>
                    
                    <div className="resource-bar">
                        <label>Fertilizer</label>
                        <div className="bar-container">
                            <div className="bar-fill fertilizer" style={{ width: `${fertilizerLevel}%` }}></div>
                        </div>
                        <span>{fertilizerLevel}%</span>
                    </div>
                </div>
                
                <div className="action-buttons">
                    <button className="action-btn water-btn" onClick={handleWater}>
                        Water
                    </button>
                    <button className="action-btn sun-btn" onClick={handleSunshine}>
                        Sunshine
                    </button>
                    <button className="action-btn fertilizer-btn" onClick={handleFertilize}>
                        Fertilize
                    </button>
                </div>
            </div>
        </div>
    );
};

export default HomeV3; 